// Почему бар не даёт отправить запрос. Правило выбора причины чистое
// (`sendBlockHint`), здесь оно собирается из состояния бара и переводится:
// у кнопки нет ни состояния, ни `t`, а у правила — React'а.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { sendBlockHint } from "./prompt-cost";

export interface UsePromptSendGateParams {
    /** `mode.minInputImages` — сколько кадров режим требует обязательно. */
    minImages: number;
    uploadedImagesCount: number;
    hasRequiredFiles: boolean;
    /** `mode.minVisualInputs` — минимум визуальных входов любого канала. */
    minVisualInputs: number;
    hasRequiredVisualInputs: boolean;
    hasElements: boolean;
    inputsNotExecutable: boolean;
    /** Длина входного видео ещё меряется — показанная цена неполная. */
    priceIncomplete: boolean;
}

export interface UsePromptSendGateResult {
    /** Режим требует обязательных кадров, которых пока нет. */
    requireFilesTooltip: boolean;
    /** Сколько картинок ещё не хватает. */
    remainingImages: number;
    /** Режим требует визуального входа (референс или видео), которого пока нет. */
    missingVisualInput: boolean;
    /**
     * Кадр обязателен и когда выбран элемент: провайдер принимает такую задачу,
     * но не исполняет её (проверено живой пробой). Общее «нужно ещё изображение»
     * в этой ситуации читается как ошибка — человек видит занятый слот элемента
     * и не понимает, чего от него хотят, поэтому причина проговаривается отдельно.
     */
    blockedWithElements: boolean;
    /** Готовый текст подсказки кнопки; null — кнопка рабочая. */
    blockedHint: string | null;
}

export function usePromptSendGate({
    minImages,
    uploadedImagesCount,
    hasRequiredFiles,
    minVisualInputs,
    hasRequiredVisualInputs,
    hasElements,
    inputsNotExecutable,
    priceIncomplete,
}: UsePromptSendGateParams): UsePromptSendGateResult {
    const { t } = useTranslation("chat");

    const requireFilesTooltip = minImages > 0 && !hasRequiredFiles;
    const remainingImages = Math.max(minImages - uploadedImagesCount, 0);
    // Кадров у режима может не быть вовсе (мультимодальный Seedance), а
    // визуальный вход при этом обязателен — своя причина, свой текст.
    const missingVisualInput = minVisualInputs > 0 && !hasRequiredVisualInputs;
    const blockedWithElements = requireFilesTooltip && hasElements;

    const blockedHint = useMemo(() => {
        const hint = sendBlockHint({
            missingRequiredImages: requireFilesTooltip,
            missingVisualInput,
            hasElements,
            inputsNotExecutable,
            priceIncomplete,
            minImages,
        });
        if (hint === null) return null;
        return hint.count === null ? t(hint.key) : t(hint.key, { count: hint.count });
    }, [
        requireFilesTooltip,
        missingVisualInput,
        hasElements,
        inputsNotExecutable,
        priceIncomplete,
        minImages,
        t,
    ]);

    return {
        requireFilesTooltip,
        remainingImages,
        missingVisualInput,
        blockedWithElements,
        blockedHint,
    };
}
